/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as React from "react";
import styled from "styled-components";
import { devices, fontFamilies, useIsMobile } from "components/shared/Styles";
import premiumVideoTall from "./images/premium-video-tall.png";
import premiumVideoTallx2 from "./images/premium-video-tall@2x.png";
import bulkUpload from "./images/bulk-upload.png";
import bulkUploadx2 from "./images/bulk-upload@2x.png";
import coverArt from "./images/cover-art.png";
import coverArtx2 from "./images/cover-art@2x.png";
import sliderDesktop from "./images/slider-desktop.svg";
import sliderMobile from "./images/slider-mobile.svg";
import backdropImage from "images/home/tile.svg";
import { PurchaseExpressClickableDiv } from "components/PurchaseExpress";
import { ExpressCheckoutButtonPosition } from "constants/Analytics";

const UpgradeFeaturesWrapper = styled.div`
  margin: 20px auto;
  max-width: 1156px;
  width: calc(100% - 40px);
  position: relative;
  display: grid;
  grid-template-columns: 3fr 1fr;
  column-gap: 48px;
  z-index: 2;

  ${devices.small} {
    width: calc(100% - 48px);
    grid-template-columns: 1fr;
  }
`;

const UpgradeFeaturesBackdrop = styled.div`
  width: 100%;
  margin: 80px auto 72px;
  padding: 60px 0;
  background-color: var(--spectrum-indigo-100);
  background-image: url(${backdropImage});
  background-position: center center;
  opacity: 44.45;
  z-index: 0;

  ${devices.medium} {
    margin: 40px auto;
    padding: 20px 0;
  }

  ${devices.small} {
    margin: 0px;
    padding: 20px 0;
  }
`;

const UpgradeFeaturesDesktopWrapper = styled.div`
  max-width: 793px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 48px;

  ${devices.small} {
    display: none;
  }
`;

const UpgradeFeaturesResponsiveWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 48px;
  max-width: 700px;

  ${devices.small} {
    width: 100%;
    gap: 24px;
  }
`;

const UpgradeFeaturesHeader = styled.h1`
  grid-column: span 2;
  text-align: center;
  height: fit-content;
  margin-bottom: 42px;
  font: normal normal 900 36px/45px ${fontFamilies};
  letter-spacing: 0px;
  color: #222222;
  opacity: 1;

  ${devices.small} {
    text-align: left;
    grid-column: span 1;
    font: normal normal 900 28px/32px ${fontFamilies};
    padding-bottom: 16px;
  }
`;

const UpgradeFeatureContainer = styled.div`
  background: #f6f6fe 0% 0% no-repeat padding-box;
  border: 3px solid #5258e415;
  width: 314px;
  border-radius: 24px;
  opacity: 1;

  ${devices.small} {
    width: 100%;
  }
`;

const UpgradeFeatureDesktopContainer = styled.div`
  background: #f6f6fe 0% 0% no-repeat padding-box;
  border: 3px solid #5258e415;
  max-width: 793px;
  width: 100%;
  border-radius: 24px;
  opacity: 1;
`;

const UpgradeFeatureDesktopInnerContainer = styled.div`
  box-sizing: border-box;
  padding: 32px;
`;

const UpgradeFeatureInnerContainer = styled.div`
  padding: 32px;
  box-sizing: border-box;
`;

const UpgrageScreenshotImage = styled.img.attrs({
  srcSet: `${premiumVideoTall} 1x, ${premiumVideoTallx2} 2x`,
})`
  object-fit: cover;
  object-position: left top;
  width: 100%;
  height: 551px;
  background: #f6f6fe 0% 0% no-repeat padding-box;
  border: 3px solid #5258e415;
  border-radius: 24px;
  box-shadow: 4px 4px 12px #00000012;
  opacity: 1;
`;

const UploadImage = styled.img.attrs({
  srcSet: `${bulkUpload} 1x, ${bulkUploadx2} 2x`,
})`
  max-width: 186px;
  width: 100%;
  margin-top: -71px;
  margin-left: -31px;
`;

const CoverArtImage = styled.img.attrs({
  srcSet: `${coverArt} 1x, ${coverArtx2} 2x`,
})`
  max-width: 244px;
  width: 100%;
  height: 89px;
  object-fit: cover;
  margin-bottom: 20px;
`;

const SliderDesktop = styled.img.attrs({
  src: sliderDesktop,
})`
  width: 100%;
  margin-bottom: -20px;
`;

const SliderMobile = styled.img.attrs({
  src: sliderMobile,
})`
  max-width: 266px;
  width: 100%;
  margin-bottom: -20px;
`;

const UpgradeFeatureHeading = styled.h2`
  text-align: left;
  font: normal normal 900 28px/28px ${fontFamilies};
  margin-bottom: 8px;
  color: #5258e4;
  opacity: 1;
`;

const UpgradeFeatureBody = styled.div`
  font: normal normal 300 18px/22px ${fontFamilies};
  line-height: 20px;
  color: #222222;
  text-align: left;
  color: #222222;
  opacity: 1;
`;

const UpgradeFeaturesDesktopPanel: React.FC<{
  position: ExpressCheckoutButtonPosition;
}> = ({ position }) => {
  return (
    <UpgradeFeaturesDesktopWrapper>
      <PurchaseExpressClickableDiv position={position}>
        <UpgrageScreenshotImage
          alt={I18n.t("bento_box.alt_text.upgrade_screenshot")}
        />
      </PurchaseExpressClickableDiv>
      <PurchaseExpressClickableDiv position={position}>
        <UpgradeFeatureDesktopContainer>
          <UpgradeFeatureDesktopInnerContainer>
            <UpgradeFeatureHeading>
              {I18n.t("bento_box.adjust_strength.heading")}
            </UpgradeFeatureHeading>
            <UpgradeFeatureBody>
              {I18n.t("bento_box.adjust_strength.body")}
            </UpgradeFeatureBody>
            <SliderDesktop />
          </UpgradeFeatureDesktopInnerContainer>
        </UpgradeFeatureDesktopContainer>
      </PurchaseExpressClickableDiv>
    </UpgradeFeaturesDesktopWrapper>
  );
};

const UpgradeFeaturesResponsivePanel: React.FC<{
  position: ExpressCheckoutButtonPosition;
}> = ({ position }) => {
  const isMobile = useIsMobile();
  return (
    <UpgradeFeaturesResponsiveWrapper>
      <PurchaseExpressClickableDiv position={position}>
        <UpgradeFeatureContainer>
          <UpgradeFeatureInnerContainer>
            <UploadImage />
            <UpgradeFeatureHeading>
              {I18n.t("bento_box.bulk_upload.heading")}
            </UpgradeFeatureHeading>
            <UpgradeFeatureBody>
              {I18n.t("bento_box.bulk_upload.body")}
            </UpgradeFeatureBody>
          </UpgradeFeatureInnerContainer>
        </UpgradeFeatureContainer>
      </PurchaseExpressClickableDiv>
      {isMobile && (
        <PurchaseExpressClickableDiv position={position}>
          <UpgradeFeatureContainer>
            <UpgradeFeatureInnerContainer>
              <UpgradeFeatureHeading>
                {I18n.t("bento_box.adjust_strength.heading")}
              </UpgradeFeatureHeading>
              <UpgradeFeatureBody>
                {I18n.t("bento_box.adjust_strength.body")}
              </UpgradeFeatureBody>
              <SliderMobile />
            </UpgradeFeatureInnerContainer>
          </UpgradeFeatureContainer>
        </PurchaseExpressClickableDiv>
      )}
      <PurchaseExpressClickableDiv position={position}>
        <UpgradeFeatureContainer>
          <UpgradeFeatureInnerContainer>
            <UpgradeFeatureHeading>
              {I18n.t("bento_box.enhance_speech.heading")}
            </UpgradeFeatureHeading>
            <UpgradeFeatureBody>
              {I18n.t("bento_box.enhance_speech.body")}
            </UpgradeFeatureBody>
          </UpgradeFeatureInnerContainer>
        </UpgradeFeatureContainer>
      </PurchaseExpressClickableDiv>
      <PurchaseExpressClickableDiv position={position}>
        <UpgradeFeatureContainer>
          <UpgradeFeatureInnerContainer>
            <CoverArtImage />
            <UpgradeFeatureHeading>
              {I18n.t("bento_box.premium_graphics.heading")}
            </UpgradeFeatureHeading>
            <UpgradeFeatureBody>
              {I18n.t("bento_box.premium_graphics.body")}
            </UpgradeFeatureBody>
          </UpgradeFeatureInnerContainer>
        </UpgradeFeatureContainer>
      </PurchaseExpressClickableDiv>
    </UpgradeFeaturesResponsiveWrapper>
  );
};

export const UpgradeFeatures: React.FC<{
  position: ExpressCheckoutButtonPosition;
}> = ({ position }) => {
  return (
    <UpgradeFeaturesBackdrop data-testid="upgrade-bento-box">
      <UpgradeFeaturesWrapper>
        <UpgradeFeaturesHeader>
          {I18n.t("bento_box.heading")}
        </UpgradeFeaturesHeader>
        <UpgradeFeaturesDesktopPanel position={position} />
        <UpgradeFeaturesResponsivePanel position={position} />
      </UpgradeFeaturesWrapper>
    </UpgradeFeaturesBackdrop>
  );
};
